import { z } from 'zod';
import { uuidSchema } from './common.js';

/**
 * Duplicate detection.
 *
 * Covers the three modules where an accidental second record is a real
 * data-quality problem: Lead, Account, Contact. Deliberately not a generic
 * "any module" feature — the matching signals (email/phone/name/website) are
 * specific to what a person or business record looks like, and forcing every
 * other module through the same shape would either not fit or fit by
 * accident.
 */
export const DUPLICATE_CHECK_MODULES = ['LEAD', 'ACCOUNT', 'CONTACT'] as const;
export type DuplicateCheckModule = (typeof DUPLICATE_CHECK_MODULES)[number];

/**
 * What a pre-create check is run against. Every field optional: a caller
 * sends whatever it has typed so far, and the matcher uses whichever signals
 * are present for that module (see `apps/api/src/modules/duplicates`).
 * `excludeId` lets a check made while *editing* an existing record leave
 * that record out of its own results.
 */
export const checkDuplicatesSchema = z.object({
  module: z.enum(DUPLICATE_CHECK_MODULES),
  /** A person's name — Lead/Contact. Never enough alone to flag a match. */
  name: z.string().trim().max(300).optional(),
  /** A business/company name — Lead's `businessName`, or Account's own `name`. */
  businessName: z.string().trim().max(300).optional(),
  email: z.string().trim().max(320).optional(),
  phone: z.string().trim().max(40).optional(),
  mobile: z.string().trim().max(40).optional(),
  website: z.string().trim().max(300).optional(),
  accountId: uuidSchema.optional(),
  excludeId: uuidSchema.optional(),
});
export type CheckDuplicatesInput = z.infer<typeof checkDuplicatesSchema>;

/** One existing record a check turned up, with enough to identify and open it. */
export interface DuplicateMatch {
  id: string;
  /** The record's own display name — a lead/contact's full name, an account's name. */
  label: string;
  email: string | null;
  phone: string | null;
  /** For a Contact match: the account it belongs to, if any. */
  accountName: string | null;
  /** Which signals matched, in order of strength — e.g. `['email']`, `['phone', 'businessName']`. */
  matchedOn: string[];
}

export interface CheckDuplicatesResult {
  matches: DuplicateMatch[];
}

export const listDuplicateGroupsQuerySchema = z.object({
  module: z.enum(DUPLICATE_CHECK_MODULES),
});
export type ListDuplicateGroupsQuery = z.infer<typeof listDuplicateGroupsQuerySchema>;

/** One reason a pair was grouped, with a rough confidence for display — `"Business name: 96%"`. */
export interface DuplicateMatchReason {
  field: string;
  label: string;
  /** 100 for an exact normalized match; lower for a fuzzy name match. */
  confidence: number;
}

export interface DuplicateGroupMember {
  id: string;
  label: string;
  email: string | null;
  phone: string | null;
  createdAt: string;
}

export interface DuplicateGroup {
  /** Stable within one response: `${recordAId}:${recordBId}`, canonically ordered. */
  key: string;
  module: DuplicateCheckModule;
  members: [DuplicateGroupMember, DuplicateGroupMember];
  reasons: DuplicateMatchReason[];
  /** Set once an admin has reviewed this exact pair; absent for a fresh suggestion. */
  reviewStatus: 'IGNORED' | 'CONFIRMED' | null;
}

export const reviewDuplicateSchema = z.object({
  module: z.enum(DUPLICATE_CHECK_MODULES),
  recordAId: uuidSchema,
  recordBId: uuidSchema,
  status: z.enum(['IGNORED', 'CONFIRMED']),
});
export type ReviewDuplicateInput = z.infer<typeof reviewDuplicateSchema>;
